import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import ReactPlayer from 'react-player';
import { OnProgressProps } from 'react-player/base';
import { IRefPhaserGame, PhaserGame } from 'Phaser/PhaserGame';
import {
  ExerciseToReactEventType,
  ListenExercisePassed,
  ListenExercisePaused,
  ListenExerciseResumed,
  ListenForPhaserEvent,
  ListenHideExercise,
  ListenObjectiveComplete,
  ListenObjectiveSkipped,
  ListenShowExercise,
  ListenSkipButtonRequest,
  ReactToExerciseEventType,
  MidiOff,
  MidiOn,
  NotifyPhaser,
  PauseExercise,
  RemoveListener,
  ResumeExercise,
  SkipExercise,
  ListenPhaserReady,
  ChangePlaybackRate,
  ToggleExercise,
  UnloadExercise,
} from 'Phaser/EventBus';
import ChapterList, { getTime, Timestamp } from '../Chapters';
import './style.css';
import Button from '@mui/material/Button';
import FastForwardRounded from '@mui/icons-material/FastForwardRounded';
import Fullscreen from 'Components/Fullscreen';
import { markChapterAsCompleted, setLastChapter } from 'Actions/tutorial';
import { useDispatch, useSelector } from 'react-redux';
import { setLevelSelect } from 'Actions/app';
import { useNavigate } from 'react-router-dom';
import Play from '../../../assets/images/Play.png';
import {
  ChapterData,
  ChapterType,
  ExerciseScene,
  InteractiveEvent,
} from 'Types/ExerciseData';
import { setPlaybackSpeedState } from 'Actions/tutorial';
import { TutorialReducerState } from 'Reducers/tutorialReducer';
import * as Immutable from 'immutable';
import { CustomCircularProgress } from 'Components/StyledComponents';
import { useMidiContext } from 'Contexts/MidiContext';
import * as StaffLine from 'Phaser/GameObjects/StaffLine';
import axios from 'axios';
import { MainAppReducer, TutorialData2, LevelStatus } from 'Types';
import * as lessonActions from 'Actions/lesson';
import Phrase from 'Models/Phrase';
import MusicXML from 'Models/MusicXML';
import TimeSignature from 'Models/TimeSignature';
import logger from 'Utils/Logger';
import * as Tone from 'tone'
import { RootState } from 'Reducers';
import { setManualPause } from 'Actions/tutorial';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { CursorData } from 'Phaser/GameObjects/StaffLine/Types';
import Logger from 'Utils/Logger';

type Visibility = 'hidden' | 'visible';

const VIDEO_SRC_PROD =
  'https://assets.museflow.ai/videos/interactive_tutorials/';

/*const TUTORIAL_VID_URL_PREFIX =
  process.env.REACT_APP_NODE_ENV === 'local'
    ? 'http://localhost:8000/'
    : VIDEO_SRC_PROD;*/
const TUTORIAL_VID_URL_PREFIX = VIDEO_SRC_PROD;

const SkipButton = ({
  skip,
  visibility,
}: {
  skip: () => void;
  visibility?: Visibility;
}) => {
  return (
    <Button
      id="skip-button"
      className={visibility}
      sx={{
        backgroundColor: '#4A5AA1',
        borderRadius: '35px',
        width: '5%',
        minWidth: 'unset',
        height: 'calc(5% * 1.777778)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'absolute',
        bottom: '10%',
        opacity: 0,
        right: '10%',
        padding: 'none',
        pointerEvents: 'all',
      }}
      variant="contained"
      onClick={() => {
        skip();
      }}
      onKeyUp={(e) => {
        if (e.key === ' ') e.preventDefault();
      }}
    >
      <FastForwardRounded sx={{ height: '100%', width: 'unset' }} />
    </Button>
  );
};

const ProceedButton = ({
  proceed,
  visibility,
}: {
  proceed: () => void;
  visibility: Visibility;
}) => {
  return (
    <Box onClick={proceed} id="proceed-button">
      <Box sx={playButtonStyles} className={visibility}>
        <img
          alt="play_button"
          style={{ maxWidth: '100%', maxHeight: '100%', margin: '0px' }}
          src={Play}
        />
      </Box>
    </Box>
  );
};

const playButtonStyles = {
  position: 'absolute',
  width: '70px',
  height: '70px',
  top: 'calc(58%)',
  left: 'calc(50% - 35px)',
  padding: '0px',
  margin: '0px',
  borderRadius: '50%',
  pointerEvents: 'all',
  '&:hover': {
    background: 'rgba(212,134,108,1.0)',
    cursor: 'pointer',
    transition: '0.3s',
    boxShadow: '0px 0px 5px 5px rgb(212,134,108,.5)',
  },
};

// These are the layouts that will be rendered!!!
const InteractiveTemplate1 = ({
  tutorialData,
}: {
  tutorialData: TutorialData2;
}) => {
  const [lastPauseEventTime, setLastPauseEventTime] = React.useState<
    number | null
  >(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {checkMidiAndShowModal} = useMidiContext();

  const [chapterData, setChapterData] = React.useState<ChapterData[]>();

  React.useEffect(() => {
    const chapters = tutorialData.tutorial_config.sections.flatMap(
      (x) => x.chapters
    );
    setChapterData(chapters);
    logger.debug('chapters:', chapters);
  }, [tutorialData]);

  type TimestampInfo = {
    load?: Timestamp;
    start?: Timestamp;
  };

  /*
   * timestamps: [chapter][objective] = {
   *   load: timestamp,
   *   start: timestamp,
   *   ...
   * }
   */
  const [timestamps, setTimestamps] = React.useState<TimestampInfo[][]>([]);

  type PhraseData = {
    cursorData: CursorData;
    uuid: string;
    phrase: Phrase;
    timeSignature: TimeSignature;
  };
  const [phrases, setPhrases] = React.useState<PhraseData[][]>([]);
  const [phaserReady, setPhaserReady] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!chapterData) return;
    const _timestamps: TimestampInfo[][] = [];
    Logger.debug('timestamps chapter data: ', chapterData);
    chapterData.forEach((chapter, chapterIdx) => {
      const [chapterType, chapterProps] = chapter.data;
      if (chapterType === ChapterType.Exercise) {
        _timestamps[chapterIdx] = [];
        let objectiveIdx = -1;
        chapterProps.events.forEach(({ at, phaserEvent }: InteractiveEvent) => {
          const [eventType] = phaserEvent;
          switch (eventType) {
            case ReactToExerciseEventType.UpdateObjective: {
              objectiveIdx++;
              _timestamps[chapterIdx][objectiveIdx] = {
                ..._timestamps[chapterIdx][objectiveIdx],
                load: at,
              };
              break;
            }
            case ReactToExerciseEventType.StartExercise: {
              _timestamps[chapterIdx][objectiveIdx] = {
                ..._timestamps[chapterIdx][objectiveIdx],
                start: at,
              };
              break;
            }
            default: {
            }
          }
        });
      }
    });
    Logger.debug('timestamps: ', _timestamps);
    setTimestamps(_timestamps);
    Promise.all(
      tutorialData.objective_data.map(async ({ chapter, phrase, objective }) => ({
        chapter,
        objective,
        cursorData: (
          await axios.get(phrase.music_xml_url_pointers.json_data_url_1440)
        ).data as CursorData,
        musicXml: (await axios.get(phrase.s3_music_xml_url)).data,
        timeSignature: new TimeSignature(
          phrase.time_signature_numerator,
          phrase.time_signature_denominator
        ),
        phraseUuid: phrase.s3_music_xml_id,
      }))
    )
      .then((phraseArray: Array<{ chapter: number; objective: number; cursorData: CursorData; musicXml: any; phraseUuid: string; timeSignature: TimeSignature }>) => {
        const phrasesNew: PhraseData[][] = [];
        phraseArray.forEach(
          ({
            chapter,
            objective,
            cursorData,
            musicXml,
            phraseUuid,
            timeSignature,
          }) => {
            const musicXmlObject = new MusicXML(musicXml, [timeSignature]);
            const phrase = new Phrase(
              phraseUuid,
              musicXmlObject,
              -1,
              '',
              '',
              cursorData.serializedIterator,
              cursorData.serializedIterator,
              cursorData.measureRects
            );
            if (phrasesNew[chapter] === undefined) phrasesNew[chapter] = [];
            phrasesNew[chapter][objective] = {
              cursorData,
              uuid: '',
              phrase,
              timeSignature,
            };
          }
        );
        setPhrases(phrasesNew);
      })
      .catch((err: unknown) => console.error(err));
  }, [tutorialData, chapterData]);

  const phaserRef = React.useRef<IRefPhaserGame | null>(null);
  const videoRef = React.useRef<ReactPlayer>(null);

  const [exercisePaused, setExercisePaused] = React.useState(true);
  const [showExercise, setShowExercise] = React.useState(false);
  const [isExercise, setIsExercise] = React.useState(false);
  const { hasInteracted, cbType } = useSelector(
    (state: MainAppReducer) => ({
      hasInteracted: state.mainAppReducer.interactedWithPage,
      cbType: state.mainAppReducer.userData?.colorblind_type
    })
  );
  const {showMidiModal, dismissedMidiModal} = useMidiContext();
  const lastChapter = useSelector((state: RootState) => {
    return Immutable.get(
      state.tutorialReducer?.lastChapter,
      tutorialData.level_number
    );
  });
  
  // Get the set of completed chapters for this level
  const completedChapters = useSelector((state: RootState) => {
    return Immutable.get(
      state.tutorialReducer?.completed,
      tutorialData.level_number
    ) as Immutable.Set<number> | undefined;
  });
  
  const [currentChapter, setCurrentChapter] = React.useState<number | undefined>(lastChapter as number);
  // || (currentChapter && currentChapter < 1 && autoplay);
  // const manualPause = useSelector((state: RootState) => state.tutorialReducer?.manualPause ?? false);
  const autoplay = (hasInteracted && !showMidiModal) || (currentChapter !== undefined && hasInteracted && currentChapter < 1);
  const [manualPause, setManualPause] = React.useState<boolean>(!autoplay);
  const [isVideoPlaying, setIsVideoPlaying] = React.useState<boolean>(autoplay);
  // const dispatchManualPause = React.useCallback((val: boolean) => dispatch(setManualPause({ manualPause: val })), [dispatch]);
  const playbackSpeed = useSelector((state: RootState) => {
    return state.tutorialReducer?.playbackSpeed ?? 1
  });

  useEffect(() => {
    Logger.log('midiNotFound', showMidiModal);
    if(showMidiModal && currentChapter !== undefined && currentChapter > 0 && !dismissedMidiModal) {
      setIsVideoPlaying(false);
      Logger.log('midiNotFound is true, setting manualPause to true');
      setManualPause(true);
      
    }
  }, [hasInteracted,showMidiModal,currentChapter,manualPause]);

  const changePlaybackSpeed = React.useCallback(() => {
    dispatch(
      setPlaybackSpeedState(playbackSpeed >= 2.0 ? 0.5 : playbackSpeed + 0.25)
    );
  }, [playbackSpeed, dispatch]);
  const [skipButtonVisibility, setSkipButtonVisibility] = React.useState<
    Visibility | undefined
  >(undefined);
  const [proceedButtonVisibility, setProceedButtonVisibility] =
    React.useState<Visibility>('hidden');
  
  // Track if level 0 completion has been fired to prevent duplicate dispatches
  const level0CompletionFiredRef = React.useRef(false);

  React.useEffect(() => {
    NotifyPhaser(ChangePlaybackRate(playbackSpeed));
  }, [playbackSpeed]);

  const phaserLoadTimer = React.useRef<any>(undefined);
  const checkChapterTimer = React.useRef<any>(undefined);

  const loadScene = React.useCallback(
    (name: string, props: any) => {
      if (phaserRef.current?.scene) {
        if (phaserLoadTimer.current) clearTimeout(phaserLoadTimer.current);
        phaserRef.current?.scene?.scene.start(name, {
          ...props,
        });
      } else {
        // Wait for phaser to initialise and try again
        if (phaserLoadTimer.current) clearTimeout(phaserLoadTimer.current);
        phaserLoadTimer.current = setTimeout(() => loadScene(name, props), 500);
      }
    },
    [phaserRef]
  );

  const onSeek = () => {};

  const retryCountRef = React.useRef<Map<number, number>>(new Map());
  
  const CheckChapter = React.useCallback(
    (chapter: number) => {
      phaserRef.current?.scene?.scene.stop();
      if (!chapterData || !tutorialData) return;
      const _chapter = chapterData[chapter || 0];
      if (!_chapter) return;
      const [type, data] = _chapter.data;
      switch (type) {
        case ChapterType.Lecture: {
          setIsExercise(false);
          retryCountRef.current.delete(chapter); // Clear retry count for this chapter
          // Mark a video chapter as complete automatically after two seconds watching
          const t = setTimeout(() => {
            dispatch(
              markChapterAsCompleted({
                lesson: tutorialData.level_number,
                chapter: chapter || 0,
              })
            );
          }, 2000);
          return () => clearTimeout(t);
        }
        case ChapterType.Exercise: {
          setIsExercise(true);
          const [name, config] = data.scene;
          //if (true) {
          const updatedConfig = {
            ...config,
            cbType,
            chapterIdx: chapter,
            objectives: config.objectives.map((obj, idx) => ({
              ...obj,
              videoSkipDestination:
                obj.videoSkipDestination ??
                timestamps.at(chapter)?.at(idx + 1)?.load ??
                chapterData.at(chapter + 1)?.start ??
                tutorialData.tutorial_config.end,
              //videoCompleteDestination: obj.videoCompleteDestination ?? timestamps.at(chapter)?.at(idx)?.load ?? chapterData.at(chapter + 1)?.start ?? tutorialData.tutorial_config.end,
            })),
          }; // as StaffExercise.Config;
          if (
            name === ExerciseScene.StaffExercise ||
            name === ExerciseScene.NoTimeExercise
          ) {
            if (phrases && phrases[chapter]) {
              retryCountRef.current.delete(chapter); // Clear retry count for this chapter
              updatedConfig.objectives = updatedConfig.objectives.map(
                (o, idx) => ({ ...o, phrases: [phrases[chapter][idx]] })
              );
              loadScene(name, updatedConfig);
            } else {
              // Check retry count to avoid infinite retries
              const retryCount = retryCountRef.current.get(chapter) || 0;
              if (retryCount < 10) {
                console.warn(`Phrases not loaded yet for chapter: ${chapter}, retry ${retryCount + 1}/10`);
                retryCountRef.current.set(chapter, retryCount + 1);
                // Retry after a short delay if phrases aren't loaded yet
                if (checkChapterTimer.current) {
                  clearTimeout(checkChapterTimer.current);
                }
                checkChapterTimer.current = setTimeout(() => {
                  // Need to get the latest CheckChapter from the ref
                  const latestCheckChapter = checkChapterRef.current;
                  if (latestCheckChapter) {
                    latestCheckChapter(chapter);
                  }
                }, 300);
              } else {
                console.error(`Failed to load phrases for chapter ${chapter} after 10 retries`, phrases);
                retryCountRef.current.delete(chapter);
              }
            }
          } else {
            retryCountRef.current.delete(chapter); // Clear retry count for this chapter
            loadScene(name, updatedConfig);
          }
          break;
        }
      }
    },
    [tutorialData, chapterData, dispatch, loadScene, phrases, timestamps]
  );

  // Store CheckChapter in a ref so it can be called from setTimeout
  const checkChapterRef = React.useRef(CheckChapter);
  React.useEffect(() => {
    checkChapterRef.current = CheckChapter;
  }, [CheckChapter]);

  const [isLoaded, setIsLoaded] = React.useState(false);
  const [initialSeekComplete, setInitialSeekComplete] = React.useState(false);


  // Side effect triggered on focused chapter change
  React.useEffect(() => {
    // Clear any pending CheckChapter calls
    if (checkChapterTimer.current) {
      clearTimeout(checkChapterTimer.current);
    }

    // Don't call CheckChapter if an exercise is currently showing
    // This prevents interrupting the exercise when video progress updates the TOC highlight
    if (showExercise) {
      return;
    }

    checkChapterTimer.current = setTimeout(() => {
      CheckChapter(currentChapter || 0);
      if (currentChapter) {
        dispatch(
          setLastChapter({
            lesson: tutorialData.level_number,
            chapter: currentChapter,
          })
        );
      }
      checkChapterTimer.current = undefined;
    }, 500);

    return () => {
      if (checkChapterTimer.current) {
        clearTimeout(checkChapterTimer.current);
        checkChapterTimer.current = undefined;
      }
    };
  }, [currentChapter, CheckChapter, dispatch, tutorialData.level_number, showExercise]);

  React.useEffect(()=>{
    if(currentChapter !== undefined && currentChapter === 1) {
      // show midi blocker after first chapter so new users get a chance to
      // experience a bit of the tutorial. Give enough time for chapter to finish.
      setTimeout(() => {
        checkMidiAndShowModal();
      }, 4500);
    } else if(currentChapter !== undefined && currentChapter > 1) {
      checkMidiAndShowModal();
    }
  },[currentChapter])


  const onProgress = React.useCallback(
    (progress: OnProgressProps) => {
      if (!chapterData) {
        console.warn('no chapter data');
        return;
      }
      const current = progress.playedSeconds;
      if (current >= getTime(tutorialData.tutorial_config.end)) {
        setProceedButtonVisibility('visible');
        
        // Mark level 0 as complete when tutorial ends AND all chapters are completed
        const allChaptersComplete = completedChapters?.size === chapterData.length;
        if (tutorialData.level_number === 0 && allChaptersComplete && !level0CompletionFiredRef.current) {
          level0CompletionFiredRef.current = true;
          const now = new Date();
          dispatch(
            lessonActions.updateCurrentULP({
              currentUserLevelProgress: {
                status: LevelStatus.complete,
                level_completed_at: now.toISOString(),
                highest_tier_complete: 1,
                highest_tier_completed_at: now.toISOString(),
              },
            })
          );
        }
      }
      const max = chapterData
        .filter((x) => getTime(x.start) <= current + 1)
        .reduce(
          (max, chapter, idx, arr) =>
            chapter.start > arr[max].start ? idx : max,
          0
        );
      
      // Don't update currentChapter from onProgress until initial seek is complete
      // This prevents overriding the saved lastChapter when video first loads
      // Note: We always update the highlighted chapter in TOC based on video progress.
      // The CheckChapter effect is separately guarded to not interrupt active exercises.
      if (initialSeekComplete) {
        setCurrentChapter(max);
      }
      //console.log("onProgress: setting chapter to ", max);
      const [type, data] = chapterData[max || 0]?.data;
      if (type === ChapterType.Exercise) {
        const ev = data.events.find((e) => {
          const diff = current - getTime(e.at);
          return diff <= 0.2 && diff >= 0;
        });
        //logger.info("progress ", current);
        /*if (ev && getTime(ev.at) - current <= 0.5)
          logger.info(current, getTime(ev.at));*/
        if (ev) {
          //eventThisFrame = true;
          if (lastPauseEventTime === getTime(ev?.at)) return;
          const { pause, phaserEvent } = ev;
          if (pause) {
            setIsVideoPlaying(false);
          }
          setLastPauseEventTime(getTime(ev.at));
          if (phaserEvent) {
            const [eventType, payload] = phaserEvent;
            if (eventType === ReactToExerciseEventType.UpdateObjective) {
              const updated = { ...payload, max, chapter: currentChapter || 0 };
              NotifyPhaser([eventType, updated]);
            } else {
              NotifyPhaser(phaserEvent);
            }
          }
        }
      }
      /*if (!eventThisFrame && current >= (lastPauseEventTime ?? 0) + 1.5)
        setLastPauseEventTime(null);*/
    },
    [
      lastPauseEventTime,
      chapterData,
      completedChapters,
      tutorialData.tutorial_config.end,
      tutorialData.level_number,
      currentChapter,
      initialSeekComplete,
      dispatch,
    ]
  );

  const playVideo = React.useCallback(() => {
    setIsVideoPlaying(true);
  }, []);

  const resume = React.useCallback(() => {
    setManualPause(false);
    // Only resume video if no exercise is showing
    // During an exercise, the video should stay paused
    /*if (!showExercise) {
      setIsPlaying(true);
    }*/
    NotifyPhaser(ResumeExercise());
  }, [showExercise]);

  const lastPlayedNoteTimer = React.useRef<NodeJS.Timeout | undefined>(undefined);

  const noteOn = React.useCallback((ev: any) => {
    //if (ev.velocity !== 0) {
    NotifyPhaser(MidiOn(ev.note, ev.velocity));
    //} else {
    //noteOff(ev); // call the note off logic becasue velocity is 0.
    //}
  }, []);

  const noteOff = React.useCallback(
    (ev: any) => NotifyPhaser(MidiOff(ev.note)),
    []
  );

  const listener = React.useRef({ name: 'eventListener', noteOn, noteOff });
  const { addListener, removeListener, clearListeners } = useMidiContext();

  React.useEffect(() => {
    // NOTE: This is entirely dependant on the video ALWAYS resuming whenever an exercise gets unloaded
    // due to whatever reason may be (the exercise being completed succesfully / skipped or jumping
    // to a completely different chapter). Point is: if the video playback state doesn't change, this
    // breaks.
    if (isVideoPlaying) {
      setSkipButtonVisibility((prev) => {
        if (prev !== undefined) return 'hidden';
        else return undefined;
      });
    }
  }, [isVideoPlaying]);

  const onVideoClick = React.useCallback((e: any) => {
    Tone.start();
    if (e.target.tagName === 'VIDEO') {
      setManualPause(true);
      NotifyPhaser(PauseExercise());
    }
  }, []);

  const jumpTo = React.useCallback(
    (seconds: number, chapter?: number) => {
      // Temporarily disable onProgress updates during manual seeks
      setInitialSeekComplete(false);
      
      setIsVideoPlaying(false);
      setManualPause(false);
      setTimeout(() => playVideo(), 500);
      setProceedButtonVisibility('hidden');
      if (videoRef && videoRef.current) videoRef.current.seekTo(seconds);
      setShowExercise(false);
      //if (type === "exercise") {
      // Instead of calling this particular function manually, use another function (or effects)
      // to handle all cases of interactive sections becoming inactive / disabled.
      setLastPauseEventTime(null);
      //phaserRef.current?.scene?.scene.stop();
      if (chapter !== undefined) {
        if (chapter === currentChapter) {
          CheckChapter(chapter);
        } else {
          setCurrentChapter(chapter);
        }
      }
      
      // Re-enable onProgress updates after seek completes
      setTimeout(() => {
        setInitialSeekComplete(true);
      }, 100);
      //NotifyPhaser();
      //}
    },
    [CheckChapter, currentChapter, playVideo]
  );

  const jumpToChapter = React.useCallback(
    (seconds: number, chapter?: number) => {
      jumpTo(seconds, chapter);
    },
    [jumpTo]
  );

  // Spacebar event listener
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        setManualPause((pause) => !pause);
        NotifyPhaser(ToggleExercise());
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Mark an exercise as complete when all of its objectives have been completed
  React.useEffect(() => {
    ListenForPhaserEvent(
      ListenExercisePassed(() => {
        dispatch(
          markChapterAsCompleted({
            lesson: tutorialData.level_number,
            chapter: currentChapter || 0,
          })
        );
      })
    );
    return () => {
      RemoveListener(ExerciseToReactEventType.ExercisePassed);
    };
  }, [currentChapter, dispatch, tutorialData.level_number]);

  const proceed = React.useCallback(() => {
    console.log('[InteractiveTemplate1] proceed - cleaning up before navigation');
    NotifyPhaser(UnloadExercise());
    
    setTimeout(() => {
      if (tutorialData.level_number === 0) {
        // Level 0 completion is already fired in onProgress when tutorial ends
        dispatch(setLevelSelect({ levelSelect: 1 }));
        navigate('/tutorial');
      } else {
        navigate('/lesson');
      }
    }, 100);
  }, [dispatch, navigate, tutorialData.level_number]);

  // Listener for "ObjectiveComplete" events
  React.useEffect(() => {
    ListenForPhaserEvent(
      ListenObjectiveComplete(({ jumpTo }: { jumpTo: Timestamp }) => {
        videoRef.current?.seekTo(getTime(jumpTo));
        setTimeout(() => playVideo(), 500);
      })
    );
    ListenForPhaserEvent(
      ListenObjectiveSkipped(({ jumpTo }: { jumpTo: Timestamp }) => {
        videoRef.current?.seekTo(getTime(jumpTo));
        playVideo();
      })
    );
    return () => {
      RemoveListener(ExerciseToReactEventType.ObjectiveComplete);
      RemoveListener(ExerciseToReactEventType.ObjectiveSkipped);
    };
  }, [playVideo]);
  

  // Other phaser event listeners
  React.useEffect(() => {
    ListenForPhaserEvent(ListenPhaserReady(() => setPhaserReady(true)));
    ListenForPhaserEvent(
      ListenShowExercise(() => {
        setShowExercise(true);
        setExercisePaused(false);
      })
    );
    ListenForPhaserEvent(ListenExercisePaused(() => setExercisePaused(true)));
    ListenForPhaserEvent(
      ListenHideExercise(() => {
        setExercisePaused(true);
        setShowExercise(false);
      })
    );
    ListenForPhaserEvent(ListenExerciseResumed(() => setExercisePaused(false)));
    ListenForPhaserEvent(
      ListenSkipButtonRequest(() => setSkipButtonVisibility('visible'))
    );
    return () => {
      RemoveListener(ExerciseToReactEventType.PhaserReady);
      RemoveListener(ExerciseToReactEventType.HideExercise);
      RemoveListener(ExerciseToReactEventType.ExercisePaused);
      RemoveListener(ExerciseToReactEventType.ExerciseResumed);
      RemoveListener(ExerciseToReactEventType.ShowExercise);
      RemoveListener(ExerciseToReactEventType.SkipButtonRequest);
    };
  }, []);

  React.useEffect(() => {
    const pauseForBackground = (reason: string) => {
      logger.debug(`[tutorial] pausing because ${reason}`);
      setManualPause(true);
      //setIsPlaying(false);
      NotifyPhaser(PauseExercise());
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        pauseForBackground('visibilitychange');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    let appStateHandle: PluginListenerHandle | undefined;
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          pauseForBackground('appStateChange');
        }
      })
        .then((handle) => {
          appStateHandle = handle;
        })
        .catch(() => {});
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      appStateHandle?.remove?.();
    };
  }, []);

  // Event listener for pausing playback when tab / window loses focus
  React.useEffect(() => {
    const blurHandler = () => {
      setManualPause(true);
      NotifyPhaser(PauseExercise());
    };
    window.addEventListener('blur', blurHandler);
    return () => window.removeEventListener('blur', blurHandler);
  }, []);

  React.useEffect(() => {
    clearListeners();
    addListener(listener.current);
    return () => removeListener(listener.current);
  }, [addListener, removeListener, clearListeners]);

  // Cleanup all timers and Phaser scene on unmount
  React.useEffect(() => {
    return () => {
      console.log('[InteractiveTemplate1] Component unmounting - cleaning up');
      
      // Stop and unload any active Phaser scene
      if (phaserRef.current?.scene) {
        try {
          console.log('[InteractiveTemplate1] Stopping Phaser scene');
          phaserRef.current.scene.scene.stop();
        } catch (err) {
          console.warn('[InteractiveTemplate1] Error stopping Phaser scene:', err);
        }
      }
      
      // Clear all timers
      if (phaserLoadTimer.current) {
        clearTimeout(phaserLoadTimer.current);
        phaserLoadTimer.current = undefined;
      }
      if (checkChapterTimer.current) {
        clearTimeout(checkChapterTimer.current);
        checkChapterTimer.current = undefined;
      }
      
      console.log('[InteractiveTemplate1] Cleanup complete');
    };
  }, []);

  // Ensure immediate stop when global manual pause turns true
  /*React.useEffect(() => {
    if (manualPause) setIsPlaying(false);
  }, [manualPause]);*/

  const aspectRatio = 1.777778;
  // Keeps track of the <video> elements width and height which is used later for scaling the canvas
  // to fit the size of the video
  const [videoPlayerSize, setVideoPlayerSize] = React.useState({
    width: 0,
    height: 0,
  });
  const [videoLoaded, setVideoLoaded] = React.useState(false);

  const onResize = React.useCallback(
    (ratio: number) => {
      const vid: HTMLVideoElement = document.getElementsByTagName('video')[0];
      if (!vid) return;
      const height = vid.clientHeight;
      const width = vid.clientWidth;
      const desiredWidth = height * ratio;
      const desiredHeight = width / ratio;
      if (desiredWidth > width) {
        setVideoPlayerSize({ width: width, height: desiredHeight });
      } else {
        setVideoPlayerSize({ width: desiredWidth, height: height });
      }
    },
    [setVideoPlayerSize]
  );

  const onVideoLoad = React.useCallback(() => {
    if (!isLoaded) {
      setIsLoaded(true);
      const vid: HTMLVideoElement = document.getElementsByTagName('video')[0];
      if (autoplay) vid.play().catch(() => setIsVideoPlaying(false));
      else {
        setIsVideoPlaying(false);
        setExercisePaused(true);
      }
      const ratio = vid.videoWidth / vid.videoHeight;
      onResize(ratio);
      setVideoLoaded(true);
      if (chapterData && currentChapter) {
        if (videoRef && videoRef.current) {
          videoRef.current.seekTo(
            getTime((chapterData[currentChapter] || chapterData[0]).start)
          );
          // Mark initial seek as complete after a short delay to allow seek to finish
          setTimeout(() => {
            setInitialSeekComplete(true);
          }, 100);
        }
      } else {
        // No saved chapter, allow onProgress to update immediately
        setInitialSeekComplete(true);
      }
    }
  }, [onResize, chapterData, isLoaded, currentChapter, autoplay]);

  // Window resize event listener
  React.useEffect(() => {
    window.addEventListener('resize', () => onResize(aspectRatio));
    return () => {
      window.removeEventListener('resize', () => onResize(aspectRatio));
    };
  }, [aspectRatio, onResize]);

  // Check if an element is within the viewport without any clipping
  const isVisible = (el: Element) => {
    const rect = el.getBoundingClientRect();
    return rect.top >= 0 && rect.bottom <= window.innerHeight;
  };

  // Scroll to current chapter
  React.useEffect(() => {
    const el = document.querySelector(`#chapter-${currentChapter}`);
    if (el && !isVisible(el)) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentChapter]);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        background: 'linear-gradient(270deg, #1D2F44 28.99%, #071423 90.95%)',
        alignItems: 'top',
        justifyContent: 'center',
      }}
    >
      {!isLoaded && (
        <Box
          sx={{
            display: 'flex',
            height: '100%',
            position: 'absolute',
            width: '100%',
            background: '#1D2F44',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
        >
          <CustomCircularProgress />
        </Box>
      )}
      <ChapterList
        sections={tutorialData.tutorial_config.sections}
        jumpToChapter={jumpToChapter}
        active={currentChapter || 0}
        lesson={tutorialData.level_number}
      />
      <Box
        onClick={onVideoClick}
        sx={{
          display: 'flex',
          margin: '20px',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          position: 'relative',
          width: '100%',
          cursor: 'pointer',
        }}
      >
        <>
          <Box
            id="svg-processor-parent"
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
            }}
          >
            <ReactPlayer
              // in order to re-pause whenever the times above are reached, the state has to stay in sync
              // with the component
              playbackRate={playbackSpeed}
              ref={videoRef}
              onPlay={playVideo}
              //controls={true}
              onSeek={onSeek}
              playing={isVideoPlaying && phaserReady && !manualPause}
              type="video/mp4"
              url={
                TUTORIAL_VID_URL_PREFIX + tutorialData.tutorial_config.videoUrl
              }
              onReady={onVideoLoad}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
              }}
              onProgress={onProgress}
              progressInterval={30 / playbackSpeed}
              width="100%"
              height="100%"
              className="react-player"
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                pointerEvents: 'none',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {
                <Box
                  sx={{
                    visibility: videoLoaded ? 'visible' : 'hidden',
                    width:
                      videoPlayerSize.width > 0 ? videoPlayerSize.width : 1280,
                    height:
                      videoPlayerSize.height > 0 ? videoPlayerSize.height : 720,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <ProceedButton
                    proceed={proceed}
                    visibility={proceedButtonVisibility}
                  />
                  <SkipButton
                    skip={() => NotifyPhaser(SkipExercise())}
                    visibility={skipButtonVisibility}
                  />
                  <Box
                    sx={{
                      zIndex: 99,
                      pointerEvents: 'all',
                      position: 'absolute',
                      bottom: '20px',
                      right: '20px',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        background: '#525969',
                        color: 'white',
                        padding: '3px 10px',
                        fontFamily: 'Lato',
                        fontWeight: 700,
                        border: '1px solid #525969',
                        ':hover': {
                          border: '1px solid white',
                          background: '#333B4D',
                        },
                      }}
                      onClick={changePlaybackSpeed}
                    >
                      <span>{`${playbackSpeed}x`}</span>
                    </Box>
                    <Box
                      sx={{
                        marginLeft: '10px',
                      }}
                    />
                    <Fullscreen show_label={false} use_dark_icon={!manualPause} />
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      visibility: showExercise ? 'visible' : 'hidden',
                    }}
                  >
                    <PhaserGame ref={phaserRef} width={1920} height={1080} />
                  </Box>
                  {manualPause && (
                    <Box
                      onClick={resume}
                      sx={{
                        background: '#000000aa',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        top: 0,
                        pointerEvents: 'all',
                      }}
                    >
                      <p style={{ fontSize: 28 }}>
                        click here, spacebar, or enter to play/pause
                      </p>
                    </Box>
                  )}
                </Box>
              }
            </Box>
          </Box>
        </>
      </Box>
    </Box>
  );
};

export default InteractiveTemplate1;